import type { ChangeEvent } from 'react';

import { useTranslation } from 'react-i18next';

import { Button, Icon } from '@vestigas/ui';
import { InputLabel } from '~/ui/molecules/TanstackForm';

import { SCREENSHOT_ACCEPT } from '../utils';

import { ScreenshotThumbnail } from './ScreenshotThumbnail';

type P = {
  readonly isBusy: boolean;
  readonly isCapturing: boolean;
  readonly isSubmitting: boolean;
  readonly onAttachFile: (
    setter: (file: File) => void,
  ) => (event: ChangeEvent<HTMLInputElement>) => void;
  readonly onCapture: () => void;
  readonly screenshot: File | undefined;
  readonly setScreenshot: (file: File | undefined) => void;
};

export function ScreenshotSection({
  isBusy = false,
  isCapturing = false,
  isSubmitting = false,
  onAttachFile,
  onCapture,
  screenshot,
  setScreenshot,
}: P) {
  const { t } = useTranslation('bugReport');

  return (
    <div className="flex flex-col">
      <InputLabel>
        <span className="inline-flex items-center gap-1">
          <Icon className="shrink-0" metaphor="photoCamera" size="sm" />
          {t(($) => $.dialog.screenshot.label)}
        </span>
      </InputLabel>

      {screenshot ? (
        <ScreenshotThumbnail
          disabled={isBusy}
          file={screenshot}
          removeLabel={t(($) => $.dialog.screenshot.removeText)}
          onClear={() => setScreenshot(undefined)}
        />
      ) : (
        <div className="flex aspect-video items-center justify-center rounded border border-dashed border-tiga-border-default bg-white">
          <div className="h-48 w-full">{/* spacer w/ 16:9 ratio */}</div>
        </div>
      )}

      <div className="mt-2 flex gap-2">
        <Button
          buttonStyle={screenshot ? 'secondary' : 'primary'}
          className="flex-1"
          disabled={isSubmitting}
          inProgress={isCapturing}
          size="small"
          onClick={onCapture}
        >
          {t(($) => $.dialog.screenshot.captureText)}
        </Button>
        <Button
          buttonStyle="secondary"
          className="flex-1"
          component="label"
          disabled={isBusy}
          size="small"
        >
          {t(($) => $.dialog.screenshot.attachFileText)}
          <input
            accept={SCREENSHOT_ACCEPT}
            hidden
            type="file"
            onChange={onAttachFile(setScreenshot)}
          />
        </Button>
      </div>
    </div>
  );
}
